import React, {useState} from 'react';
import {message, useIntl} from '@sail/intl';
import {view} from '@sail/ui';
import {isValidCryptoAddress} from '@stripe-internal/crypto-utility';

import {ScreenContentLayout} from 'src/return/ScreenContentLayout';
import {useSubmitReturnDetailsCollectionSession} from 'src/return/api/useSubmitReturnDetailsCollectionSession';
import {setValue, useSelector} from 'src/return/state/store';
import {useReturnDetailsCollectionContext} from 'src/return/state/useReturnDetailsCollectionContext';
import {upper} from 'src/utils/helpers';
import {FormTextField} from 'src/v2/components/anyCrypto/FormTextField';
import {Banner, Button, Text} from 'src/v2/components/common';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';

const messages = {
  title: message({
    id: 'crypto.payins.return.address.title',
    description: 'Title of the refund address collection screen',
    defaultMessage: 'Refund address',
  }),
  description: message<{
    cryptocurrency: React.ReactNode;
    network: React.ReactNode;
  }>({
    id: 'crypto.payins.return.address.description',
    description:
      'Instructions for entering a refund wallet address on the required network',
    defaultMessage:
      "Enter the wallet address where you'd like your refund sent. Confirm it supports {cryptocurrency} on {network} network.",
  }),
  descriptionWithoutCryptocurrency: message<{
    network: React.ReactNode;
  }>({
    id: 'crypto.payins.return.address.description_without_cryptocurrency',
    description:
      'Instructions for entering a refund wallet address when the refund token is unavailable',
    defaultMessage:
      "Enter the wallet address where you'd like your refund sent. Confirm it supports {network} network.",
  }),
  addressLabel: message({
    id: 'crypto.payins.return.address.address_label',
    description: 'Label for the refund wallet address input',
    defaultMessage: 'Refund wallet address',
  }),
  addressPlaceholder: message({
    id: 'crypto.payins.return.address.address_placeholder',
    description: 'Placeholder for the refund wallet address input',
    defaultMessage: 'Enter refund wallet address',
  }),
  invalidAddress: message<{
    network: string;
  }>({
    id: 'crypto.payins.return.address.invalid_address',
    description: 'Error message for an invalid refund wallet address',
    defaultMessage: 'Enter a valid {network} address',
  }),
  networkLabel: message<{
    network: string;
  }>({
    id: 'crypto.payins.return.address.network_label',
    description: 'Label showing the network for the refund wallet address',
    defaultMessage: 'Refund network: {network}',
  }),
  submit: message({
    id: 'crypto.payins.return.address.submit',
    description: 'Button to submit a refund wallet address',
    defaultMessage: 'Submit',
  }),
  submitError: message({
    id: 'crypto.payins.return.address.submit_error',
    description: 'Error shown when a refund wallet address cannot be submitted',
    defaultMessage:
      "We couldn't save your refund address. Please check the address and try again.",
  }),
};

export const ReturnAddressScreen = (): React.ReactElement => {
  const intl = useIntl();
  const buttonColor = useSelector(
    (state) => state.merchantBranding.buttonColor,
  );
  const cryptocurrency = useSelector((state) => state.cryptocurrency);
  const network = useSelector((state) => state.network!);
  const {clientSecret, sessionId} = useReturnDetailsCollectionContext();
  const [submitReturnDetails, {loading}] =
    useSubmitReturnDetailsCollectionSession({clientSecret, sessionId});
  const [walletAddress, setWalletAddress] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const trimmedWalletAddress = walletAddress.trim();
  const isAddressValid =
    !trimmedWalletAddress.length ||
    isValidCryptoAddress(trimmedWalletAddress, network);
  const displayNetwork = upper(network);
  const addressError =
    trimmedWalletAddress.length > 0 && !isAddressValid
      ? intl.formatMessage(messages.invalidAddress, {
          network: displayNetwork,
        })
      : undefined;
  const disabled = !trimmedWalletAddress.length || !!addressError;

  const handleSubmit = async () => {
    setSubmitError(null);

    try {
      const collectionSession = await submitReturnDetails(trimmedWalletAddress);
      setValue((state) => {
        state.submittedWalletAddress = collectionSession.submittedWalletAddress;
        state.state = 'completed';
      });
    } catch {
      setSubmitError(intl.formatMessage(messages.submitError));
    }
  };

  return (
    <ScreenContentLayout
      bottom={
        <Button
          onClick={handleSubmit}
          disabled={disabled || loading}
          loading={loading}
          css={{backgroundColor: buttonColor ?? undefined}}
        >
          {intl.formatMessage(messages.submit)}
        </Button>
      }
    >
      <>
        <view.div css={{stack: 'y', alignX: 'center'}}>
          <Text type="labelEmphasized">
            {intl.formatMessage(messages.title)}
          </Text>
        </view.div>

        <view.div
          css={{
            stack: 'y',
            gap: 'medium',
          }}
        >
          <Text type="body">
            {cryptocurrency
              ? intl.formatMessage(messages.description, {
                  cryptocurrency: (
                    <view.span
                      key="cryptocurrency"
                      css={{fontWeight: 'semibold'}}
                    >
                      {formatAssetCode(cryptocurrency)}
                    </view.span>
                  ),
                  network: (
                    <view.span key="network" css={{fontWeight: 'semibold'}}>
                      {displayNetwork}
                    </view.span>
                  ),
                })
              : intl.formatMessage(messages.descriptionWithoutCryptocurrency, {
                  network: (
                    <view.span key="network" css={{fontWeight: 'semibold'}}>
                      {displayNetwork}
                    </view.span>
                  ),
                })}
          </Text>

          <view.div css={{stack: 'y', gap: 'xsmall'}}>
            <FormTextField
              label={intl.formatMessage(messages.addressLabel)}
              placeholder={intl.formatMessage(messages.addressPlaceholder)}
              value={walletAddress}
              onChange={setWalletAddress}
              error={addressError}
            />
            <Text type="caption" css={{color: 'subdued'}}>
              {intl.formatMessage(messages.networkLabel, {
                network: displayNetwork,
              })}
            </Text>
          </view.div>

          {submitError ? <Banner message={submitError} /> : null}
        </view.div>
      </>
    </ScreenContentLayout>
  );
};
